// API client for the Presidência da Estaca Worker

const BASE_URL = '/api/v1';

// Filters out undefined/null values before building query string
// Prevents URLSearchParams from sending literal "undefined" strings to the worker
function buildQuery(params?: Record<string, string | number | undefined | null>): string {
  if (!params) return '';
  const filtered = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  ) as Record<string, string>;
  const q = new URLSearchParams(filtered).toString();
  return q ? `?${q}` : '';
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Users
export const usersApi = {
  list: () => request<{ data: User[] }>('/users'),
  me: () => request<{ data: User }>('/users/me'),
  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return request<{ data: { photo_url: string } }>(`/users/${id}/photo`, {
      method: 'PUT',
      body: form,
      headers: {}, // Let browser set content-type with boundary
    });
  },
};

// Dashboard
export const dashboardApi = {
  get: () => request<{ data: DashboardData }>('/dashboard'),
};

// Tasks
export const tasksApi = {
  list: (params?: { assigned_to?: string; status?: string; sort?: string }) =>
    request<{ data: Task[] }>(`/tasks${buildQuery(params)}`),
  create: (data: { title: string; assigned_to: string; deadline?: string }) =>
    request<{ data: Task }>('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Task>) =>
    request<{ data: Task }>(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  complete: (id: string) =>
    request<{ data: Task }>(`/tasks/${id}/complete`, { method: 'PUT' }),
  reopen: (id: string) =>
    request<{ data: Task }>(`/tasks/${id}/reopen`, { method: 'PUT' }),
};

// Interviews
export const interviewsApi = {
  list: (params?: { status?: string; assigned_to?: string; sort?: string }) =>
    request<{ data: Interview[] }>(`/interviews${buildQuery(params)}`),
  create: (data: { person_name: string; subject: string; subject_detail?: string }) =>
    request<{ data: Interview }>('/interviews', { method: 'POST', body: JSON.stringify(data) }),
  schedule: (id: string, data: {
    assigned_to: string;
    scheduled_date: string;
    scheduled_start_time: string;
    scheduled_end_time: string;
  }) => request<{ data: Interview }>(`/interviews/${id}/schedule`, {
    method: 'PUT', body: JSON.stringify(data),
  }),
  update: (id: string, data: Partial<Interview>) =>
    request<{ data: Interview }>(`/interviews/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  complete: (id: string, data: { completed_date?: string; notes?: string }) =>
    request<{ data: Interview }>(`/interviews/${id}/complete`, { method: 'PUT', body: JSON.stringify(data) }),
  revert: (id: string) =>
    request<{ data: Interview }>(`/interviews/${id}/revert`, { method: 'PUT' }),
  delete: (id: string) =>
    request<{ data: { deleted: boolean } }>(`/interviews/${id}`, { method: 'DELETE' }),
  checkConflicts: (params: { assigned_to: string; date: string; start: string; end: string; exclude_id?: string }) =>
    request<{ data: Interview[]; has_conflicts: boolean }>(`/interviews/conflicts${buildQuery(params)}`),
};

// Callings
export const callingsApi = {
  list: (params?: { status?: string; type?: string; ward?: string }) =>
    request<{ data: Calling[] }>(`/callings${buildQuery(params)}`),
  get: (id: string) => request<{ data: Calling }>(`/callings/${id}`),
  create: (data: {
    person_name: string;
    ward: string;
    type: string;
    recommended_by: string;
    calling_name: string;
    sustain_scope: string;
  }) => request<{ data: Calling }>('/callings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Calling>) =>
    request<{ data: Calling }>(`/callings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateStep: (id: string, step: string, value: boolean, options?: { date?: string; by?: string }) =>
    request<{ data: Calling }>(`/callings/${id}/step`, {
      method: 'PUT',
      body: JSON.stringify({ step, value, ...options }),
    }),
  archive: (id: string) =>
    request<{ data: Calling }>(`/callings/${id}/archive`, { method: 'PUT' }),
  generateSustainText: (ids: string[]) =>
    request<{ data: { text: string } }>('/callings/generate-sustain-text', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
};

// Meetings
export const meetingsApi = {
  list: (params?: { status?: string; type?: string }) =>
    request<{ data: Meeting[] }>(`/meetings${buildQuery(params)}`),
  get: (id: string) => request<{ data: Meeting }>(`/meetings/${id}`),
  create: (data: { type: string; date: string; time?: string }) =>
    request<{ data: Meeting }>('/meetings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: { date?: string; time?: string; status?: string; presidida_por?: string; dirigida_por?: string; oracao_abertura?: string; pensamento?: string; oracao_encerramento?: string }) =>
    request<{ data: Meeting }>(`/meetings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ data: { deleted: boolean } }>(`/meetings/${id}`, { method: 'DELETE' }),
  getAgenda: (id: string) =>
    request<{ data: MeetingAgendaItem[] }>(`/meetings/${id}/agenda`),
  addAgendaItem: (id: string, data: { category: string; content: string }) =>
    request<{ data: MeetingAgendaItem }>(`/meetings/${id}/agenda`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  updateAgendaItem: (id: string, itemId: string, data: Partial<MeetingAgendaItem>) =>
    request<{ data: MeetingAgendaItem }>(`/meetings/${id}/agenda/${itemId}`, {
      method: 'PUT', body: JSON.stringify(data),
    }),
  deleteAgendaItem: (id: string, itemId: string) =>
    request<{ data: { deleted: boolean } }>(`/meetings/${id}/agenda/${itemId}`, { method: 'DELETE' }),
  reorderAgenda: (id: string, order: string[]) =>
    request<{ data: { reordered: boolean } }>(`/meetings/${id}/agenda/reorder`, {
      method: 'PUT', body: JSON.stringify({ order }),
    }),
  downloadAgendaPdf: (id: string) => `${BASE_URL}/meetings/${id}/agenda/pdf`,
  downloadAgendaDocx: (id: string) => `${BASE_URL}/meetings/${id}/agenda/docx`,
  uploadMinutes: (id: string, file: File) => {
    const form = new FormData();
    form.append('minutes', file);
    return request<{ data: { minutes_file_url: string } }>(`/meetings/${id}/minutes`, {
      method: 'PUT',
      body: form,
      headers: {},
    });
  },
};

// Settings
export const settingsApi = {
  get: () => request<{ data: UserSettings }>('/settings'),
  update: (data: Partial<UserSettings>) =>
    request<{ data: UserSettings }>('/settings', { method: 'PUT', body: JSON.stringify(data) }),
};

// Activity
export const activityApi = {
  list: (params?: { user_id?: string; entity_type?: string; from?: string; to?: string; limit?: number; offset?: number }) =>
    request<{ data: ActivityLog[]; total: number }>(`/activity${buildQuery(params)}`),
};

// Notifications (in-app)
export interface AppNotification {
  id: string;
  type: 'meeting_today' | 'interview_today' | 'interview_scheduled' | 'task_assigned';
  title: string;
  body: string;
  entity_id: string;
  path: string;
  created_at: string;
  is_today: boolean;
}

export const notificationsApi = {
  list: () => request<{ data: AppNotification[] }>('/notifications'),
};

// Push notifications
export const pushApi = {
  getVapidKey: () => request<{ data: { key: string } }>('/push/vapid-public-key'),
  subscribe: (subscription: PushSubscriptionJSON) =>
    request<{ data: { subscribed: boolean } }>('/push/subscribe', {
      method: 'POST', body: JSON.stringify({ subscription }),
    }),
  unsubscribe: (subscription?: PushSubscriptionJSON) =>
    request<{ data: { unsubscribed: boolean } }>('/push/unsubscribe', {
      method: 'DELETE', body: JSON.stringify({ subscription }),
    }),
};

// Type imports (from shared)
import type {
  User, Task, Interview, Calling, Meeting, MeetingAgendaItem,
  ActivityLog, UserSettings, DashboardData
} from '../../../shared/types';

export type { User, Task, Interview, Calling, Meeting, MeetingAgendaItem, ActivityLog, UserSettings, DashboardData };
export { ApiError };
